/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

/*============================================================
  MATERIAL UI
============================================================*/
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid2";

import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

/*============================================================
  PROPRIETARY - COMPONENTS
============================================================*/
import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridTextfield from "../../widgets/GridTextfield";
import GridDropdown from "../../widgets/GridDropdown";

/*============================================================
  PROPRIETARY - REDUX DATA SLICES
============================================================*/
import { updateClientInfo } from "../../../states/slcPersonalInformation.js";

/*============================================================
  PROPRIETARY - DATA MODELS
============================================================*/
import { createPersonalInfo } from "./PersonalInfoModel.js";

/*============================================================
  PROPRIETARY - UTILS
============================================================*/
import countryPhoneCode from "../../../data/countryPhoneCodes.json";
import { createPayload, createReduxAction } from "../../../utils/modelHelper.js";
import { ConstructionOutlined, Today } from "@mui/icons-material";
import { setSelectionRange } from "@testing-library/user-event/dist/utils/index.js";

const titleData = [
  { key: "MR", value: "Mr" },
  { key: "MRS", value: "Mrs" },
  { key: "MDM", value: "Mdm" },
  { key: "MS", value: "Ms" },
  { key: "DR", value: "Dr" },
];

const raceData = [
  { key: "CHI", value: "Chinese" },
  { key: "IND", value: "Indian" },
  { key: "MAL", value: "Malay" },
  { key: "OTH", value: "Others" },
];

const identityTypeData = [
  { key: "NRIC", value: "NRIC" },
  { key: "Passport", value: "Passport" },
  { key: "FIN", value: "FIN" },
];

const genderData = [
  { key: "M", value: "Male" },
  { key: "F", value: "Female" },
];

const maritalStatusData = [
  { key: "S", value: "Single" },
  { key: "M", value: "Married" },
  { key: "W", value: "Widowed" },
  { key: "D", value: "Divorced" },
];

const booleanData = [
  { key: "Y", value: "Yes" },
  { key: "N", value: "No" },
];

export default function SectionNameAndIdentification() {
  const currApplicant = useSelector((state) => state.app.currApplicant);
  const personalInfo = useSelector((state) => state.personalInformation[currApplicant]);

  const [title, setTitle] = useState(personalInfo.title);
  const [fullName, setFullName] = useState(personalInfo.fullName);
  const [race, setRace] = useState(personalInfo.race);
  const [gender, setGender] = useState(personalInfo.gender);
  const [dob, setDob] = useState(dayjs(formatDate(personalInfo.dob)));
  const [countryOfBirth, setCountryOfBirth] = useState(personalInfo.countryOfBirth);
  const [countryOfNationality, setCountryOfNationality] = useState(personalInfo.countryOfNationality);
  const [maritalStatus, setMaritalStatus] = useState(personalInfo.maritalStatus);
  const [idType, setIdType] = useState(personalInfo.idType);
  const [idNumber, setIdNumber] = useState(personalInfo.idNumber);
  const [isPoliticallyExposed, setIsPoliticallyExposed] = useState(personalInfo.isPoliticallyExposed);

  const [titleError, setTitleError] = useState("");
  const [fullNameError, setFullNameError] = useState("");
  const [raceError, setRaceError] = useState("");
  const [genderError, setGenderError] = useState("");
  const [dobError, setDobError] = useState("");
  const [countryOfBirthError, setCountryOfBirthError] = useState("");
  const [countryOfNationalityError, setCountryOfNationalityError] = useState("");
  const [maritalStatusError, setMaritalStatusError] = useState("");
  const [idTypeError, setIdTypeError] = useState("");
  const [idNumberError, setIdNumberError] = useState("");
  const [isPoliticallyExposedError, setIsPoliticallyExposedError] = useState("");

  const dispatch = useDispatch();

  //REFRESH COMPONENT WITH CURRENT APPLICANTE DATA
  useEffect(() => {
    setTitle(personalInfo.title);
    setFullName(personalInfo.fullName);
    setRace(personalInfo.race);
    setGender(personalInfo.gender);
    setDob(dayjs(formatDate(personalInfo.dob)));
    setCountryOfBirth(personalInfo.countryOfBirth);
    setCountryOfNationality(personalInfo.countryOfNationality);
    setMaritalStatus(personalInfo.maritalStatus);
    setIdType(personalInfo.idType);
    setIdNumber(personalInfo.idNumber);
    setIsPoliticallyExposed(personalInfo.isPoliticallyExposed);
  }, [currApplicant]);

  /**
   * Given DD MM YYYY --> MM DD YYYY
   *
   * MUI DatePicker uses AdapterDayjs that reliesd on dayjs(). To initilize dayjs(), "MM DD YYYY" format is used.
   * Eg. dayjs("MM DD YYYY") will display "DD MM YYYY" on DatePicker UI component.
   *
   * Data is stores as "DD MM YYYY" on Redux. When date is read from Redux, it needs to reformat given to dayjs()
   * Eg. From Redux - DD MM YYYY  --> formatDate() --> MM DD YYYY --> dayjs("MM DD YYYY")
   *
   * dayjs() without parameter returns today's date.
   */
  function formatDate(dayMonthYear) {
    let dateArr = dayMonthYear.split(" ");
    let monthDayYear = dateArr.length < 3 ? dayjs() : dateArr[1] + " " + dateArr[0] + " " + dateArr[2]; //MM DD YYYY
    return monthDayYear;
  }

  //CALLED WHEN "NEXT BUTTON" IS PRESSED. WHEN ATTEMPTING TO MOVE TO NEXT PAGE.
  function validateForm(e) {
    e.preventDefault();
  }

  /*
    CHANGE ACTION HANDLER
    1. Dropdowns sends Pointer from mouse clicks
    2. TextFields sends SyntheticBasedEvent from typing
  */
  function handleChange(e, object) {
    let id = "";

    let actionType = "";
    let updateAction = null;
    let payload = null;

    const personalInfo = createPersonalInfo();

    if (e instanceof PointerEvent) {
      id = object.props.id;

      if (id === "title") {
        personalInfo.title = e.target.value;
        setTitle(e.target.value);
        actionType = "UPDATE_TITLE";
      }

      if (id === "race") {
        personalInfo.race = e.target.value;
        setRace(e.target.value);
        actionType = "UPDATE_RACE";
      }

      if (id === "gender") {
        personalInfo.gender = e.target.value;
        setGender(e.target.value);
        actionType = "UPDATE_GENDER";
      }

      if (id === "countryOfBirth") {
        personalInfo.countryOfBirth = e.target.value;
        setCountryOfBirth(e.target.value);
        actionType = "UPDATE_COUNTRY_OF_BIRTH";
      }

      if (id === "countryOfNationality") {
        personalInfo.countryOfNationality = e.target.value;
        setCountryOfNationality(e.target.value);
        actionType = "UPDATE_COUNTRY_OF_NATIONALITY";
      }

      if (id === "maritalStatus") {
        personalInfo.maritalStatus = e.target.value;
        setMaritalStatus(e.target.value);
        actionType = "UPDATE_MARITAL_STATUS";
      }

      if (id === "idtype") {
        personalInfo.idType = e.target.value;
        setIdType(e.target.value);
        actionType = "UPDATE_ID_TYPE";
      }

      if (id === "isPoliticallyExposed") {
        personalInfo.isPoliticallyExposed = e.target.value;
        setIsPoliticallyExposed(e.target.value);
        actionType = "UPDATE_IS_POLITICALLY_EXPOSED";
      }
    } else {
      id = e.target.id;

      if (id === "fullname") {
        if (e.target.value.length === 0) {
          setFullNameError("Full name is required.");
        } else if (e.target.value.length > 70) {
          setFullNameError("Full name exceeded 70 characters");
        } else setFullNameError("");

        personalInfo.fullName = e.target.value;
        setFullName(e.target.value);
        actionType = "UPDATE_FULLNAME";
      }

      if (id === "idnumber") {
        const reNumbers = /^[0-9\b]+$/;

        if (e.target.value.length === 0) {
          setIdNumberError("Id number is required.");
        } else if (e.target.value.length > 70) {
          setIdNumberError("Id number exceeded 70 characters");
        } else setIdNumberError("");

        personalInfo.idNumber = e.target.value;
        setIdNumber(e.target.value);
        actionType = "UPDATE_ID_NUMBER";
      }
    }

    payload = createPayload(currApplicant, personalInfo);
    updateAction = createReduxAction(actionType, payload);
    dispatch(updateClientInfo(updateAction));
  }

  function handleDobChange(date) {
    const today = new Date();
    setDob(date);

    const personalInfo = createPersonalInfo();
    let actionType = "";
    let updateAction = null;
    let payload = null;

    let day = date.$D;
    let month = date.$M;
    let year = date.$y;

    //DISPATCH - SELECTED DATE OF BIRTH
    personalInfo.dob = day + " " + (month + 1) + " " + year;
    actionType = "UPDATE_DOB";
    payload = createPayload(currApplicant, personalInfo);
    updateAction = createReduxAction(actionType, payload);
    dispatch(updateClientInfo(updateAction));

    //Calculate Age
    let age = today.getFullYear() - date.$y;
    let monthDifference = today.getMonth() - date.$M;
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < date.$D)) age--;

    if (Number(age) < 16) setDobError("Must be at 16 years old to apply");
    else {
      setDobError("");
    }

    //DISPATCH - AGE
    personalInfo.age = age;
    actionType = "UPDATE_AGE";
    payload = createPayload(currApplicant, personalInfo);
    updateAction = createReduxAction(actionType, payload);
    dispatch(updateClientInfo(updateAction));
  }

  return (
    <>
      <Box component="form" onSubmit={validateForm} noValidate>
        <Grid container spacing={2}>
          <GridSectionHeader
            sectionName="Name & Identifiication"
            sx={{
              p: 1,
              pl: 1.8,
              mt: 0,
              fontSize: 16,
              fontWeight: "bold",
              color: "#FFFFFF", //WHITE
              // color: "#000000", //BLACK
              // backgroundColor: "#d9d9d9",
              backgroundColor: "#10B981",
            }}
          />

          <GridDropdown
            id="title"
            key="title"
            label="Title"
            defaultValue=""
            currState={title}
            onChange={handleChange}
            data={titleData}
            error={titleError}
          />

          <GridTextfield
            id="fullname"
            label="Full Name (As per NRIC/Passport)"
            currState={fullName}
            onChange={handleChange}
            error={fullNameError}
            helperText={fullNameError}
          />

          <GridDropdown
            id="race"
            label="Race"
            columnSize="4"
            defaultValue=""
            currState={race}
            onChange={handleChange}
            data={raceData}
            error={raceError}
          />
          <GridDropdown
            id="gender"
            label="Gender"
            columnSize="4"
            defaultValue=""
            currState={gender}
            onChange={handleChange}
            data={genderData}
            error={genderError}
          />

          <Grid size={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                id="dob"
                format="DD MMM YYYY"
                label="Data of Birth"
                value={dob}
                onChange={(newDate) => handleDobChange(newDate)}
                error={setDobError}
              />
            </LocalizationProvider>
            {dobError && <FormHelperText error={dobError}>Must be at 16 years old.</FormHelperText>}
          </Grid>

          <GridDropdown
            id="countryOfBirth"
            label="Country Of Birth"
            columnSize="4"
            defaultValue=""
            currState={countryOfBirth}
            onChange={handleChange}
            data={countryPhoneCode}
            dataType="country"
            error={countryOfBirthError}
          />

          <GridDropdown
            id="countryOfNationality"
            label="Country Of Nationality"
            columnSize="4"
            defaultValue=""
            currState={countryOfNationality}
            onChange={handleChange}
            data={countryPhoneCode}
            dataType="country"
            error={countryOfNationalityError}
          />

          <GridDropdown
            id="maritalStatus"
            label="Marital Status"
            columnSize="4"
            defaultValue=""
            currState={maritalStatus}
            onChange={handleChange}
            data={maritalStatusData}
            error={maritalStatusError}
          />

          <GridDropdown
            id="idtype"
            label="Identity Type"
            defaultValue=""
            currState={idType}
            onChange={handleChange}
            data={identityTypeData}
          />

          <GridTextfield
            id="idnumber"
            label="Identification Number"
            currState={idNumber}
            onChange={handleChange}
            error={idNumberError}
            helperText={idNumberError}
          />

          <GridDropdown
            id="isPoliticallyExposed"
            label="Politically Exposed Person"
            defaultValue=""
            currState={isPoliticallyExposed}
            onChange={handleChange}
            data={booleanData}
            error={isPoliticallyExposedError}
          />
        </Grid>
      </Box>
    </>
  );
}
